/**
 * Архив аккаунтов — акки со статусами «Бан» и «Выключение».
 *
 * Это ПРЕДСТАВЛЕНИЕ, а не отдельное хранилище: акк остаётся привязан к
 * телефону и модели, его снапшоты и просмотры продолжают попадать в графики
 * и агрегаты (они нужны для расчёта конверта просмотров в фанов и прочей
 * аналитики). Архив просто убирает мёртвые акки из рабочих таблиц.
 *
 * Вернуть акк в работу = сменить ему статус (инлайн в таблице) — он сразу
 * уйдёт из архива обратно в рабочие списки.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@wml-insta/shared';
import { ARCHIVED_STATUSES, isArchivedStatus } from '@wml-insta/shared';
import { useAccountsData } from '../lib/useAccountsData.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { formatNum } from '../lib/format.js';
import { AccountsTable } from '../ui/AccountsTable.js';
import { AccountFilters, EMPTY_FILTERS, filterAccounts, type AccountFiltersValue } from '../ui/AccountFilters.js';
import { PhoneTabs } from '../ui/PhoneTabs.js';

export function ArchivePage({ me }: { me: User }) {
  const data = useAccountsData('archived');
  const nav = useNavigate();
  const [filters, setFiltersRaw] = useState<AccountFiltersValue>(
    () => ({ ...EMPTY_FILTERS, ...loadPersisted(me.id, 'filters.archive', {}) }),
  );
  const setFilters = (next: AccountFiltersValue) => {
    setFiltersRaw(next);
    savePersisted(me.id, 'filters.archive', next);
  };

  const archived = useMemo(
    () => data.accounts.filter((a) => isArchivedStatus(a.status) && !data.archiveGrace.has(a.id)),
    [data.accounts, data.archiveGrace],
  );
  const filtered = useMemo(() => filterAccounts(archived, filters), [archived, filters]);

  if (!data.loaded) return <div className="empty-state">Загрузка…</div>;

  const banned = archived.filter((a) => a.status === 'Бан').length;
  const off = archived.filter((a) => a.status === 'Выключение').length;
  const viewsTotal = archived.reduce((s, a) => s + a.viewsTotal, 0);

  return (
    <div className="page-inner page-fill">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>Архив аккаунтов</h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            {formatNum(archived.length)} акк. · бан {formatNum(banned)} · выключение {formatNum(off)}
            {' · '}{formatNum(viewsTotal)} просмотров учтено в статистике
            {filtered.length !== archived.length && ` · показано ${formatNum(filtered.length)}`}
          </div>
        </div>
        <div style={{ color: 'var(--text-subtle)', fontSize: 12, maxWidth: 460, textAlign: 'right', lineHeight: 1.45 }}>
          Просмотры архивных акков продолжают учитываться в графиках и статистике
          телефонов и моделей. Чтобы вернуть акк в работу — смени ему статус в таблице.
        </div>
      </div>

      {data.error && <div className="error" style={{ marginBottom: 12 }}>{data.error}</div>}

      <AccountsTable
        accounts={filtered}
        me={me}
        phones={data.phones}
        models={data.models}
        tableKey="archive"
        onPatched={data.applyPatched}
        onDeleted={data.applyDeleted}
        onOpen={(a) => nav(`/account/${a.id}`)}
        onError={data.setError}
        toolbar={
          <AccountFilters
            value={filters}
            onChange={setFilters}
            accounts={archived}
            models={data.models}
            phones={data.phones}
            statusOptions={ARCHIVED_STATUSES}
            allowNoStatus={false}
          />
        }
      />

      <PhoneTabs
        phones={data.phones}
        activeId={filters.phoneIds.length === 1 ? filters.phoneIds[0]! : null}
        onSelect={(id) => setFilters({ ...filters, phoneIds: id ? [id] : [] })}
      />
    </div>
  );
}
